import React, { Fragment, useState, useContext } from "react";
import { Dialog, Popover, Transition, Menu } from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  Bars3Icon,
  XMarkIcon,
  FingerPrintIcon,
  SquaresPlusIcon,
  ListBulletIcon,
  CurrencyDollarIcon,
  ClipboardDocumentCheckIcon,
  ScaleIcon,
} from "@heroicons/react/24/outline";

import logo from "../images/logo.png";
import { AppContext } from "../context/AppContext";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

export default function Header() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, signOut } = useContext(AppContext);
  const navigate = useNavigate();

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  const handleSignout = (e) => {
    e.preventDefault();
    signOut();
    navigate("/signin", { replace: true });
  };

  return (
    <header className="pl-4 pr-4 md:pr-14 md:px-5 border-gray-100 border border-solid shadow-sm">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between lg:px-8"
        aria-label="Global"
      >
        {/* Mobile menu button */}
        <div className="flex items-center justify-between flex-1 lg:hidden">
          <Link to="/" className="flex items-center">
            <img className="h-auto w-28 md:w-28" src={logo} alt="logo" />
          </Link>
          <div className="flex items-center">
            <div className="block mr-2">
              <LanguageSwitcher />
            </div>
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 ml-3"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">{t("Open main menu")}</span>
              <Bars3Icon className="h-8 w-8 md:h-8 md:w-8" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Main navigation */}
        <div className=" hidden lg:flex flex-1 items-center justify-between">
          <Link to="/" className="flex items-center">
            <img className="h-auto w-20 md:w-28" src={logo} alt="logo" />
          </Link>
          <Popover.Group className="flex gap-x-3 md:gap-x-5 lg:gap-x-6 ">
            <Link
              to="/"
              className="sm:text-xs md:text-sm lg:text-lg font-bold leading-3 text-gray-900 hover:bg-pink-500 hover:text-white px-0 sm:px-1 md:px-2 lg:px-2 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-0 transition duration-300 ease-in-out"
            >
              {t("Home")}
            </Link>
            <Popover className="relative">
              {({ open }) => (
                <>
                  <Popover.Button
                    className={`${
                      open ? "text-gray-900" : "text-black"
                    } text-xs sm:text-sm md:text-sm lg:text-lg font-bold leading-6 hover:bg-pink-500 hover:text-white px-0 sm:px-1 md:px-2 lg:px-2 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-0 transition duration-300 ease-in-out`}
                  >
                    <span>{t("Tools")}</span>
                    <ChevronDownIcon
                      className={`${
                        open
                          ? "rotate-180 text-gray-600"
                          : "rotate-0 text-black"
                      } ml-2 h-5 w-5 group-hover:text-gray-500 inline transition-transform duration-300 ease-in-out`}
                      aria-hidden="true"
                    />
                  </Popover.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute z-10 mt-3 w-screen max-w-md transform px-2 sm:px-0 lg:max-w-3xl">
                      <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-2">
                          {[
                            {
                              name: t("Checklist"),
                              href: "/ChecklistPage",
                              icon: ListBulletIcon,
                            },
                            {
                              name: t("Budget Tracker"),
                              href: "/BudgetTrackerPage",
                              icon: CurrencyDollarIcon,
                            },
                            {
                              name: t("Guest Management"),
                              href: "/GuestManagement",
                              icon: FingerPrintIcon,
                            },
                            {
                              name: t("Digital Mariage Invitation"),
                              href: "/DMariageInvitation",
                              icon: SquaresPlusIcon,
                            },
                          ].map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className="-m-3 flex items-start rounded-lg p-3 hover:bg-gray-50"
                            >
                              <item.icon
                                className="h-6 w-6 flex-shrink-0 text-pink-600"
                                aria-hidden="true"
                              />
                              <div className="ml-4">
                                <p className="text-base font-medium text-gray-900">
                                  {item.name}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>

            <Link
              to="/aboutus"
              className="text-xs sm:text-sm md:text-sm lg:text-lg font-bold leading-6 text-gray-900 hover:bg-pink-500 hover:text-white px-0 sm:px-1 md:px-2 lg:px-2 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-0 transition duration-300 ease-in-out"
            >
              {t("About us")}
            </Link>
            <Popover className="relative">
              {({ open }) => (
                <>
                  <Popover.Button
                    className={`${
                      open ? "text-gray-900" : "text-black"
                    } text-xs sm:text-sm md:text-sm lg:text-lg font-bold leading-6 hover:bg-pink-500 hover:text-white px-0 sm:px-1 md:px-2 lg:px-2 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-0 transition duration-300 ease-in-out`}
                  >
                    <span>{t("Legal Education")}</span>
                    <ChevronDownIcon
                      className={`${
                        open
                          ? "rotate-180 text-gray-600"
                          : "rotate-0 text-black"
                      } ml-2 h-5 w-5 group-hover:text-gray-500 inline transition-transform duration-300 ease-in-out`}
                      aria-hidden="true"
                    />
                  </Popover.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute z-10 mt-3 w-screen max-w-md transform px-2 sm:px-0 lg:max-w-3xl">
                      <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                        <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-2">
                          {[
                            {
                              name: t("Legal Education"),
                              href: "/LegalEducation",
                              icon: ScaleIcon,
                            },
                            {
                              name: t("Tawkil services "),
                              href: "/dashboard/tawkil-service",
                              icon: ClipboardDocumentCheckIcon,
                            },
                          ].map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className="-m-3 flex items-start rounded-lg p-3 hover:bg-gray-50"
                            >
                              <item.icon
                                className="h-6 w-6 flex-shrink-0 text-pink-600"
                                aria-hidden="true"
                              />
                              <div className="ml-4">
                                <p className="text-base font-medium text-gray-900">
                                  {item.name}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
            <Link
              to="/blogs"
              className="text-xs sm:text-sm md:text-sm lg:text-lg font-bold leading-6 text-gray-900 hover:bg-pink-500 hover:text-white px-0 sm:px-1 md:px-2 lg:px-2 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-0 transition duration-300 ease-in-out"
            >
              {t("Blogs")}
            </Link>
          </Popover.Group>

          <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                <span className="inline-block h-10 w-10 overflow-hidden rounded-full bg-gray-100">
                  <svg
                    className="h-full w-full text-gray-300"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </span>
                <div className="relative flex items-center space-x-2 md:space-x-2">
                  <Link
                    to="/dashboard/profile"
                    className="block text-sm md:text-sm font-bold text-black text-center"
                  >
                    {user?.firstName} {user?.lastName}
                  </Link>

                  <Menu as="div" className="relative inline-block text-left">
                    <div>
                      <Menu.Button className="flex items-center rounded-full bg-gray-100 text-black hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                        <span className="sr-only">{t("Open options")}</span>
                        <EllipsisVerticalIcon
                          className="h-5 w-5 md:h-6 md:w-6"
                          aria-hidden="true"
                        />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-40 sm:w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-right">
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <Link
                                to="/dashboard/profile"
                                className={classNames(
                                  active
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-700",
                                  "block px-4 py-2 text-sm text-right"
                                )}
                              >
                                {t("Dashboard")}
                              </Link>
                            )}
                          </Menu.Item>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="submit"
                                className={classNames(
                                  active
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-700",
                                  "block w-full px-4 py-2 text-right text-sm"
                                )}
                                onClick={handleSignout}
                              >
                                {t("Sign-out")}
                              </button>
                            )}
                          </Menu.Item>
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
                <div className="hidden lg:block">
                  <LanguageSwitcher />
                </div>
              </>
            ) : (
              <Fragment>
                <div className="hidden lg:flex space-x-2 md:space-x-4 lg:space-x-6 xl:space-x-8">
                  <Link
                    to="/signin"
                    className="rounded-md bg-pink-600 px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm md:px-3 md:py-2 md:text-sm lg:px-4 lg:py-2.5 lg:text-sm xl:px-4 xl:py-2.5 xl:text-sm font-semibold text-white shadow-sm hover:bg-pink-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-500 transition duration-300 ease-in-out"
                  >
                    {t("Sign in")}
                  </Link>
                  <Link
                    to="/signup"
                    className="rounded-md bg-pink-50 px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm md:px-3 md:py-2 md:text-sm lg:px-4 lg:py-2.5 lg:text-sm xl:px-4 xl:py-2.5 xl:text-sm font-semibold text-pink-600 shadow-sm hover:bg-pink-100 transition duration-300 ease-in-out"
                  >
                    {t("Sign up")}
                  </Link>
                  <div className="hidden lg:block">
                    <LanguageSwitcher />
                  </div>
                </div>
              </Fragment>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-10" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="-m-1.5 p-1.5"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">{t("Your Company")}</span>
              <img className="h-auto w-28 md:w-28" src={logo} alt="" />
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">{t("Close menu")}</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="py-6 space-y-2">
                <Link
                  to="/"
                  className="text-lg font-bold leading-6 text-gray-900 block hover:bg-pink-500 hover:text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-2 transition duration-300 ease-in-out"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("Home")}
                </Link>

                <Popover className="relative">
                  {({ open }) => (
                    <>
                      <Popover.Button
                        className={`${
                          open ? "text-black" : "text-black"
                        } text-lg font-bold leading-6 text-gray-900 hover:bg-pink-500 hover:text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-2 transition duration-300 ease-in-out w-full text-left`}
                      >
                        <span>{t("Tools")}</span>
                        <ChevronDownIcon
                          className={`${
                            open ? "rotate-180" : "rotate-0"
                          } ml-2 h-5 w-5 inline transition-transform duration-300 ease-in-out`}
                          aria-hidden="true"
                        />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute z-10 mt-3 w-full max-w-sm transform px-2 sm:px-0 lg:max-w-lg">
                          <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                            <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                              {[
                                {
                                  name: t("Checklist"),
                                  href: "/ChecklistPage",
                                  icon: ListBulletIcon,
                                },
                                {
                                  name: t("Budget Tracker"),
                                  href: "/BudgetTrackerPage",
                                  icon: CurrencyDollarIcon,
                                },
                                {
                                  name: t("Guest Management"),
                                  href: "/GuestManagement",
                                  icon: FingerPrintIcon,
                                },
                                {
                                  name: t("Digital Mariage Invitation"),
                                  href: "/DMariageInvitation",
                                  icon: SquaresPlusIcon,
                                },
                              ].map((item) => (
                                <Link
                                  key={item.name}
                                  to={item.href}
                                  className="-m-3 flex items-start rounded-lg p-3 hover:bg-gray-50"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  <item.icon
                                    className="h-6 w-6 flex-shrink-0 text-pink-600"
                                    aria-hidden="true"
                                  />
                                  <div className="ml-4">
                                    <p className="text-base font-medium text-gray-900">
                                      {item.name}
                                    </p>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover>
                <Link
                  to="/aboutus"
                  className="text-lg font-bold leading-6 text-gray-900 block hover:bg-pink-500 hover:text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-2 transition duration-300 ease-in-out"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("About Us")}
                </Link>
                <Popover className="relative">
                  {({ open }) => (
                    <>
                      <Popover.Button
                        className={`${
                          open ? "text-gray-900" : "text-black"
                        } text-lg font-bold leading-6 hover:bg-pink-500 hover:text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-2 transition duration-300 ease-in-out w-full text-left`}
                      >
                        <span>{t("Legal Education")}</span>
                        <ChevronDownIcon
                          className={`${
                            open
                              ? " rotate-180"
                              : " rotate-0"
                          } ml-2 h-5 w-5 inline transition-transform duration-300 ease-in-out`}
                          aria-hidden="true"
                        />
                      </Popover.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Popover.Panel className="absolute z-10 mt-3 w-full max-w-sm transform px-2 sm:px-0 lg:max-w-lg">
                          <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                            <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                              {[
                                {
                                  name: t("Legal Education"),
                                  href: "/LegalEducation",
                                  icon: ScaleIcon,
                                },
                                {
                                  name: t("Tawkil services"),
                                  href: "/dashboard/tawkil-service",
                                  icon: ClipboardDocumentCheckIcon,
                                },
                              ].map((item) => (
                                <Link
                                  key={item.name}
                                  to={item.href}
                                  className="-m-3 flex items-start rounded-lg p-3 hover:bg-gray-50"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  <item.icon
                                    className="h-6 w-6 flex-shrink-0 text-pink-600"
                                    aria-hidden="true"
                                  />
                                  <div className="ml-4">
                                    <p className="text-base font-medium text-gray-900">
                                      {item.name}
                                    </p>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover>
                <Link
                  to="/blogs"
                  className="text-lg font-bold leading-6 text-gray-900 block hover:bg-pink-500 hover:text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-2 transition duration-300 ease-in-out"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("Blogs")}
                </Link>

                {isAuthenticated ? (
                  <div>
                    <div className="flex flex-col space-y-4">
                      <Link
                        to="/dashboard/profile"
                        className="text-lg font-bold leading-6 text-gray-900 block hover:bg-pink-500 hover:text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-offset-2 transition duration-300 ease-in-out"
                      >
                        {t("Dashboard")}
                      </Link>
                      <Link
                        to="/signout"
                        className="text-lg font-bold leading-6 ml-3 text-white block bg-red-500 hover:bg-red-600 px-2 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2 transition duration-300 ease-in-out w-36 text-center"
                        onClick={handleSignout}
                      >
                        {t("Sign-out")}
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="py-6 space-y-4">
                    <Link
                      to="/signin"
                      className="block rounded-md bg-pink-600 ml-3 px-2.5 py-1.5 text-sm font-semibold text-white shadow-md hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-700 transition duration-300 w-28 ease-in-out"
                    >
                      {t("Sign in")}
                    </Link>
                    <Link
                      to="/signup"
                      className="block w-28 rounded-md ml-3 bg-pink-50 px-2.5 py-1.5 text-sm font-semibold text-pink-600 shadow-md hover:bg-pink-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-700 transition duration-300 ease-in-out"
                    >
                      {t("Sign up")}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
}
